/**
 * <MediaThumb /> — reusable artwork/media thumbnail with graceful fallback.
 *
 * Renders public artwork via a plain <img src> (no auth needed for
 * non-evidence files). Uses the existing <AuthImage /> pipeline for
 * evidence/private files. On load-error OR missing/corrupt file, shows
 * a polished "Preview unavailable" state instead of the browser's
 * broken-image icon.
 */
import React, { useState } from 'react';
import { ImageOff } from 'lucide-react';
import theme from '../theme';
import AuthImage from './AuthImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaThumb({ id, url, filename, kind, style, className }) {
  const [errored, setErrored] = useState(false);
  const isPrivate = kind === 'evidence';
  const finalUrl = url || (id ? `/api/nft/file/${id}` : null);

  if (errored || !finalUrl) {
    return (
      <div
        data-testid="media-thumb-fallback"
        className={className}
        style={{
          ...style,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 4,
          background: theme.colors.bg.secondary,
          border: `1px dashed ${theme.colors.borders}`,
          color: theme.colors.text.muted,
        }}
      >
        <ImageOff size={20} />
        <span style={{ fontSize: 10, fontWeight: 600 }}>Preview unavailable</span>
      </div>
    );
  }

  if (isPrivate) {
    return (
      <AuthImage
        url={finalUrl}
        alt={filename || ''}
        style={style}
        className={className}
        onErrorFallback={
          <div style={{
            ...style, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 4,
            background: theme.colors.bg.secondary,
            border: `1px dashed ${theme.colors.borders}`,
            color: theme.colors.text.muted,
          }}>
            <ImageOff size={20} />
            <span style={{ fontSize: 10, fontWeight: 600 }}>Preview unavailable</span>
          </div>
        }
      />
    );
  }

  return (
    <img
      src={`${BACKEND_URL}${finalUrl}`}
      alt={filename || ''}
      style={style}
      className={className}
      onError={() => setErrored(true)}
    />
  );
}
